/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import * as React from 'react';
import classNames from 'classnames';
import Icon from '../Icon';
import { usePagination, DOTS } from '../../hooks/usePagination';
import { useWindowDimensions } from '../../hooks/useWindowDimensions';

export interface PaginationProps {
  id?: string;
  currentPage?: number;
  totalPageCount?: number;
  siblingCount?: number;
  handlePagination: (page: number | string) => void;
  className?: string;
  isSmall?: boolean;
}

const MOBILE_VIEW: number = 512;

const Pagination: React.FC<PaginationProps> = (props) => {
  const {
    id,
    currentPage = 1,
    totalPageCount = 7,
    siblingCount = 1,
    handlePagination,
    className,
    isSmall = false,
  } = props;

  const { width } = useWindowDimensions();
  const isMobile: boolean = width <= MOBILE_VIEW || isSmall;

  const paginationClassName = classNames(className, 'ui-pagination');

  const paginationRange = usePagination({
    totalPageCount,
    siblingCount,
    currentPage,
    isMobile,
  });

  const lastPage = paginationRange[paginationRange.length - 1];

  function showArrows(pageCount: number): boolean {
    if (!isMobile && pageCount > 7) {
      return true;
    }

    if (isMobile && pageCount > 5) {
      return true;
    }

    return false;
  }

  return (
    <ul id={id} className={paginationClassName}>
      {showArrows(totalPageCount) && (
        <li
          className={classNames('ui-pagination-icon', {
            'ui-pagination-icon--disabled': currentPage === 1,
          })}
          onClick={() => {
            handlePagination(currentPage - 1);
          }}
          onKeyDown={() => {
            handlePagination(currentPage - 1);
          }}
          key="left-icon"
        >
          <Icon name="chevron-left" size="xx-small" />
        </li>
      )}
      <li>
        <ul className="ui-pagination-items">
          {paginationRange.map((p, index) => {
            if (p === DOTS) {
              return (
                <li
                  key={`${p + index}`}
                  className="ui-pagination-item ui-pagination-item-dots"
                  onClick={() => {
                    handlePagination(index === 1 || index === 0 ? 1 : lastPage);
                  }}
                  onKeyDown={() => {
                    handlePagination(index === 1 || index === 0 ? 1 : lastPage);
                  }}
                >
                  {p}
                </li>
              );
            }

            return (
              <li
                key={`${p.toString() + index}`}
                className={classNames('ui-pagination-item', {
                  'ui-pagination-item--active': currentPage === p,
                })}
                onClick={() => {
                  handlePagination(p);
                }}
                onKeyDown={() => {
                  handlePagination(p);
                }}
              >
                {p}
              </li>
            );
          })}
        </ul>
      </li>
      {showArrows(totalPageCount) && (
        <li
          className={classNames('ui-pagination-icon', {
            'ui-pagination-icon--disabled': currentPage === lastPage,
          })}
          onClick={() => {
            handlePagination(currentPage + 1);
          }}
          onKeyDown={() => {
            handlePagination(currentPage + 1);
          }}
          key="right-icon"
        >
          <Icon name="chevron-right" size="xx-small" />
        </li>
      )}
    </ul>
  );
};

export default Pagination;
